import { ClaimWorkflowStatusResponse, CoreFetch, WorkerApi } from '../../api';
import { AutomationRule } from './automation/worker-automation.types';
import { normalizeAutomationRuleForPersist } from './automation/worker-automation.utils';
import { WorkerConfigurationApiMode } from './worker-configuration.api';

/** What the platform's matcher said about one claim. */
export interface IDenialRuleTestResult {
    /** True when the matcher picked the candidate rule for this claim. */
    matched: boolean;
    /** The workflow status the platform returned for the claim, passed through uninterpreted. */
    workflowStatus?: string;
}

/**
 * Runs ONE candidate denial rule against ONE claim, on the platform's own matcher.
 *
 * This is the only way to ask the live matcher from the front end: enter-worker receives the chosen
 * `ruleId` from the platform instead of matching claims itself. It is NOT a backtest — one claim, as it
 * is now — so nothing built on it may present a count or a dollar amount (FE-75, decided 2026-09-08).
 *
 * `matched` is "the matcher returned a rule id", not "it returned THIS id": the request carries exactly
 * one rule, so any id that comes back is that rule, and comparing ids would turn a server that re-keys
 * rules into a silent "no match".
 *
 * The response also carries the claim's full details. They are dropped here on purpose, so no caller
 * can render patient data it never asked for.
 */
export async function testDenialRuleAgainstClaim(
    claimId: string,
    rule: AutomationRule,
    apiMode: WorkerConfigurationApiMode,
): Promise<IDenialRuleTestResult> {
    const workerRuleTesterPayload = {
        claimId: claimId.trim(),
        rule: { ...normalizeAutomationRuleForPersist(rule), priority: 1 },
    };
    const api = CoreFetch<WorkerApi>(WorkerApi);
    const response: ClaimWorkflowStatusResponse =
        apiMode === 'provider'
            ? await api.testDenialRuleConfigurationAgainstClaimProvider({ workerRuleTesterPayload })
            : await api.testDenialRuleConfigurationAgainstClaim({ workerRuleTesterPayload });

    return { matched: Boolean(response.ruleId), workflowStatus: response.workflowStatus };
}
